import { useState } from 'react';

import chevronLeftIcon from '@shared/assets/images/chevron-left_icon.svg';
import chevronRightIcon from '@shared/assets/images/chevron-right_black_icon.svg';

import ReferenceDocumentModal from '../ReferenceDocumentModal/ReferenceDocumentModal';

import styles from './ReferenceDocumentSection.module.scss';

/**
 * ReferenceDocumentSection 컴포넌트
 * - 참조문서 섹션 컴포넌트 (Figma: container : referencedocs, node-id=1640-14454)
 * - 섹션 타이틀 + 페이지네이션 + 테이블 (상신자, 제목) 구조
 * - 한 번에 하나의 문서만 표시하고 페이지네이션으로 이동
 * - CommentSection과 유사한 구조
 *
 * @param {Array} referenceDocuments - 참조문서 목록
 * @param {Function} onReferenceDocumentsChange - 참조문서 변경 핸들러
 * @param {boolean} isPreview - 미리보기 모드 여부
 * @param {Function} onDocumentClick - 문서 클릭 핸들러 (상세 모달 열기)
 */
function ReferenceDocumentSection({
  referenceDocuments = [],
  onReferenceDocumentsChange,
  isPreview = false,
  onDocumentClick,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // 현재 표시할 문서
  const currentDoc = referenceDocuments.length > 0 ? referenceDocuments[currentIndex] : null;
  const senderName = currentDoc?.sender || currentDoc?.drafter || currentDoc?.name || '-';
  const docTitle = currentDoc?.title || '-';
  const totalCount = referenceDocuments.length;
  const hasMultipleDocs = totalCount >= 2;

  // 이전 문서로 이동
  function handlePrev() {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }

  // 다음 문서로 이동
  function handleNext() {
    if (currentIndex < totalCount - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }

  // 이전 버튼 비활성화 여부
  const isPrevDisabled = currentIndex === 0;
  // 다음 버튼 비활성화 여부
  const isNextDisabled = currentIndex >= totalCount - 1;

  // 참조문서 선택 핸들러
  function handleReferenceSelect(selectedList) {
    if (onReferenceDocumentsChange) {
      onReferenceDocumentsChange(selectedList);
    }
    setModalOpen(false);
    // 선택 후 첫 번째 문서로 이동
    setCurrentIndex(0);
  }

  // 문서 클릭 핸들러
  function handleDocClick() {
    if (onDocumentClick && currentDoc) {
      onDocumentClick(currentDoc);
    }
  }

  // 문서가 없을 때의 렌더링
  if (referenceDocuments.length === 0) {
    return (
      <>
        <div className={styles.referenceSection}>
          <div className={styles.sectionHeaderContainer}>
            <h3 className={styles.sectionTitle}>참조문서</h3>
            {!isPreview && (
              <button
                type="button"
                className={styles.selectButton}
                onClick={function () {
                  setModalOpen(true);
                }}
              >
                선택
              </button>
            )}
          </div>
          <div className={styles.tableContainer}>
            <div className={styles.tableTopLine} />
            <div className={styles.documentRow}>
              <div className={styles.documentField}>
                <label className={styles.label}>상신자</label>
                <span className={styles.value}>-</span>
              </div>
              <div className={styles.documentField}>
                <label className={styles.label}>제목</label>
                <span className={styles.value}>-</span>
              </div>
            </div>
            <div className={styles.tableBottomLine} />
          </div>
        </div>

        {!isPreview && (
          <ReferenceDocumentModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onSelect={handleReferenceSelect}
            selectedDocuments={referenceDocuments}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={styles.referenceSection}>
        {/* 섹션 헤더 - 타이틀 + 선택버튼/페이지네이션 */}
        <div className={styles.sectionHeaderContainer}>
          <h3 className={styles.sectionTitle}>참조문서</h3>
          <div className={styles.headerControls}>
            {!isPreview && (
              <button
                type="button"
                className={styles.selectButton}
                onClick={function () {
                  setModalOpen(true);
                }}
              >
                선택
              </button>
            )}
            {hasMultipleDocs && (
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={`${styles.paginationButton} ${isPrevDisabled ? styles.disabled : ''}`}
                  onClick={handlePrev}
                  disabled={isPrevDisabled}
                  aria-label="이전 문서"
                >
                  <img src={chevronLeftIcon} alt="" className={styles.paginationIcon} />
                </button>
                <span className={styles.paginationText}>
                  {currentIndex + 1}/{totalCount}
                </span>
                <button
                  type="button"
                  className={`${styles.paginationButton} ${isNextDisabled ? styles.disabled : ''}`}
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  aria-label="다음 문서"
                >
                  <img src={chevronRightIcon} alt="" className={styles.paginationIcon} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 테이블 컨테이너 - Figma: Frame 427321522 */}
        <div className={styles.tableContainer}>
          {/* 상단 파란색 라인 */}
          <div className={styles.tableTopLine} />

          {/* 문서 정보 행 - 상신자와 제목을 한 줄에 배치 */}
          <div
            className={`${styles.documentRow} ${onDocumentClick ? styles.clickable : ''}`}
            onClick={handleDocClick}
            role={onDocumentClick ? 'button' : undefined}
            tabIndex={onDocumentClick ? 0 : undefined}
            onKeyDown={function (e) {
              if (onDocumentClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleDocClick();
              }
            }}
          >
            {/* 상신자 필드 */}
            <div className={styles.documentField}>
              <label className={styles.label}>상신자</label>
              <span className={styles.value}>{senderName}</span>
            </div>
            {/* 제목 필드 */}
            <div className={styles.documentField}>
              <label className={styles.label}>제목</label>
              <span className={styles.value}>{docTitle}</span>
            </div>
          </div>

          {/* 하단 구분선 */}
          <div className={styles.tableBottomLine} />
        </div>
      </div>

      {!isPreview && (
        <ReferenceDocumentModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSelect={handleReferenceSelect}
          selectedDocuments={referenceDocuments}
        />
      )}
    </>
  );
}

export default ReferenceDocumentSection;
